import { POINT_PICKER_VIEW_SOURCE } from './map-scripts.generated';
/** Keep remote airports/home points outside the initial view of the destination. */
export function getPointPickerView(coordinates, focus) {
    // Keep this function self contained: the same implementation runs in WebViews.
    const valid = (p) => Boolean(p && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])
        && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90);
    if (valid(focus))
        return { center: [focus[0], focus[1]], zoom: 14 };
    const unique = new Map();
    for (const p of coordinates)
        if (valid(p))
            unique.set(`${p[0].toFixed(6)}:${p[1].toFixed(6)}`, [p[0], p[1]]);
    const points = [...unique.values()].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (!points.length)
        return null;
    const radians = Math.PI / 180;
    const distance = (a, b) => {
        const h = Math.sin((b[1] - a[1]) * radians / 2) ** 2
            + Math.cos(a[1] * radians) * Math.cos(b[1] * radians)
                * Math.sin((b[0] - a[0]) * radians / 2) ** 2;
        return 12742 * Math.asin(Math.sqrt(Math.min(1, h)));
    };
    let cluster = [];
    let bestDistance = Infinity;
    for (const anchor of points) {
        const neighbors = [];
        let totalDistance = 0;
        for (const point of points) {
            const km = distance(anchor, point);
            if (km <= 25) {
                neighbors.push(point);
                totalDistance += km;
            }
        }
        if (neighbors.length > cluster.length || (neighbors.length === cluster.length && totalDistance < bestDistance)) {
            cluster = neighbors;
            bestDistance = totalDistance;
        }
    }
    if (cluster.length === 1)
        return { center: cluster[0], zoom: 13 };
    // Unwrap longitudes so a cluster crossing the date line stays a local view.
    const anchorLng = cluster[0][0];
    const longitudes = cluster.map(p => p[0] + 360 * Math.round((anchorLng - p[0]) / 360));
    const west = Math.min(...longitudes), east = Math.max(...longitudes);
    const south = Math.min(...cluster.map(p => p[1])), north = Math.max(...cluster.map(p => p[1]));
    const mercatorY = (lat) => Math.log(Math.tan(Math.PI / 4 + Math.max(-85, Math.min(85, lat)) * radians / 2));
    const ySouth = mercatorY(south), yNorth = mercatorY(north);
    const latitude = (2 * Math.atan(Math.exp((ySouth + yNorth) / 2)) - Math.PI / 2) / radians;
    const span = Math.max((east - west) / 360, (yNorth - ySouth) / (2 * Math.PI));
    return {
        center: [((west + east) / 2 + 540) % 360 - 180, latitude],
        // Conservative fallback for native maps; web maps fit the actual container.
        zoom: Math.max(0, Math.min(13, Math.log2(220 / (512 * Math.max(span, 1e-9))))),
        bounds: [[west, south], [east, north]],
    };
}
export const POINT_PICKER_VIEW_SCRIPT = `window.getPointPickerView = (${POINT_PICKER_VIEW_SOURCE});`;
